import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useRealtimeSubscription } from '../lib/realtime';
import type { TeamMember, CertAlert, LeaveRequestRow } from '../types';

function startOfWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

function tsToHours(ts: string): number {
  const d = new Date(ts);
  return d.getHours() + d.getMinutes() / 60;
}

interface UseTeamResult {
  teamMembers: TeamMember[];
  certAlerts: CertAlert[];
  leaveRequests: LeaveRequestRow[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useTeam(businessId: string | undefined, otThreshold: number): UseTeamResult {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [certAlerts, setCertAlerts] = useState<CertAlert[]>([]);
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    if (!businessId) { setLoading(false); return; }
    setLoading(true);
    setError(null);

    const weekStart = startOfWeek(new Date());
    const weekStartStr = formatDate(weekStart);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    const weekEndStr = formatDate(weekEnd);
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    const thirtyDaysStr = formatDate(thirtyDaysFromNow);

    try {
      const [usersRes, shiftsRes, certsRes, leaveRes, contractorRes, operatorRes] = await Promise.all([
        supabase
          .from('users')
          .select('id, first_name, last_name, role, employment_type, status, hourly_rate, avatar_url, phone, email, app_activated, activated_at, created_at, commission_type, commission_rate, commission_on_tips, portal_slug, portal_active')
          .eq('business_id', businessId)
          .order('role', { ascending: true }),

        supabase
          .from('schedule_entries')
          .select('id, assigned_to, start_at, end_at, status, type')
          .eq('business_id', businessId)
          .eq('type', 'shift')
          .gte('start_at', `${weekStartStr}T00:00:00`)
          .lte('start_at', `${weekEndStr}T23:59:59`)
          .neq('status', 'cancelled'),

        supabase
          .from('certifications')
          .select('id, user_id, name, type, expiry_date, no_expiry, status')
          .eq('business_id', businessId),

        supabase
          .from('leave_requests')
          .select('id, user_id, date, end_date, type, reason, status')
          .eq('business_id', businessId)
          .order('date', { ascending: true }),

        supabase
          .from('contractor_profiles')
          .select('user_id, company_name, trade_or_specialty, flat_rate, tax_status')
          .eq('business_id', businessId),

        supabase
          .from('operator_profiles')
          .select('user_id, display_name, chair_number')
          .eq('business_id', businessId),
      ]);

      const users = usersRes.data ?? [];
      const shifts = shiftsRes.data ?? [];
      const certs = certsRes.data ?? [];
      const leaves = leaveRes.data ?? [];
      const contractors = contractorRes.data ?? [];
      const operators = operatorRes.data ?? [];

      // Build indexes
      const contractorMap = new Map(contractors.map(c => [c.user_id, c]));
      const operatorMap = new Map(operators.map(o => [o.user_id, o]));

      // Entries (shifts) by user — assigned_to is an array
      const shiftsByUser = new Map<string, typeof shifts>();
      for (const s of shifts) {
        const uid = Array.isArray(s.assigned_to) ? s.assigned_to[0] : null;
        if (!uid) continue;
        const arr = shiftsByUser.get(uid) ?? [];
        arr.push(s);
        shiftsByUser.set(uid, arr);
      }

      // Certs by user
      const certsByUser = new Map<string, typeof certs>();
      for (const c of certs) {
        const arr = certsByUser.get(c.user_id) ?? [];
        arr.push(c);
        certsByUser.set(c.user_id, arr);
      }

      // Build team members
      const members: TeamMember[] = users.map(u => {
        const userShifts = shiftsByUser.get(u.id) ?? [];
        const userCerts = certsByUser.get(u.id) ?? [];

        let totalHours = 0;
        let lastDate: string | null = null;
        for (const s of userShifts) {
          const startH = tsToHours(s.start_at);
          const endH = tsToHours(s.end_at);
          totalHours += Math.max(0, endH - startH);
          const entryDate = s.start_at?.split('T')[0] ?? '';
          if (!lastDate || entryDate > lastDate) lastDate = entryDate;
        }

        const overtime = Math.max(0, totalHours - otThreshold);
        const certExpiring = userCerts.filter(c =>
          !c.no_expiry && c.expiry_date && c.expiry_date <= thirtyDaysStr && c.status !== 'expired'
        ).length;

        const cp = contractorMap.get(u.id);
        const op = operatorMap.get(u.id);

        return {
          id: u.id,
          firstName: u.first_name,
          lastName: u.last_name,
          role: u.role,
          employmentType: u.employment_type,
          status: u.status,
          hourlyRate: u.hourly_rate,
          avatarUrl: u.avatar_url,
          phone: u.phone,
          email: u.email,
          activatedAt: u.activated_at,
          appActivated: u.app_activated,
          hoursThisPeriod: Math.round(totalHours * 100) / 100,
          overtimeHours: Math.round(overtime * 100) / 100,
          shiftsThisPeriod: userShifts.length,
          lastShiftDate: lastDate,
          certCount: userCerts.filter(c => c.status === 'valid').length,
          certExpiringSoon: certExpiring,
          commissionType: u.commission_type ?? 'none',
          commissionRate: u.commission_rate ?? null,
          commissionOnTips: u.commission_on_tips ?? 'operator_keeps',
          portalSlug: u.portal_slug ?? null,
          portalActive: u.portal_active ?? false,
          contractorProfile: cp ? { companyName: cp.company_name, tradeOrSpecialty: cp.trade_or_specialty, flatRate: cp.flat_rate, taxStatus: cp.tax_status } : undefined,
          operatorProfile: op ? { displayName: op.display_name, chairNumber: op.chair_number } : undefined,
        };
      });

      setTeamMembers(members);

      // Cert alerts
      const alerts: CertAlert[] = [];
      const userNameMap = new Map(users.map(u => [u.id, `${u.first_name} ${u.last_name}`]));
      for (const c of certs) {
        if (c.status === 'expired' || (!c.no_expiry && c.expiry_date && c.expiry_date <= thirtyDaysStr)) {
          alerts.push({
            id: c.id,
            userId: c.user_id,
            userName: userNameMap.get(c.user_id) ?? 'Unknown',
            certName: c.name,
            certType: c.type,
            expiryDate: c.expiry_date,
            status: c.status,
          });
        }
      }
      alerts.sort((a, b) => (a.expiryDate ?? '').localeCompare(b.expiryDate ?? ''));
      setCertAlerts(alerts);

      // Leave requests
      const leaveRows: LeaveRequestRow[] = leaves.map(l => ({
        id: l.id,
        userId: l.user_id,
        userName: userNameMap.get(l.user_id) ?? 'Unknown',
        date: l.date,
        endDate: l.end_date,
        type: l.type,
        reason: l.reason,
        status: l.status,
      }));
      setLeaveRequests(leaveRows);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load team data');
    } finally {
      setLoading(false);
    }
  }, [businessId, otThreshold]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const rtTables = useMemo(() => ['users', 'certifications', 'leave_requests'], []);
  useRealtimeSubscription(businessId, rtTables, fetchAll);

  return { teamMembers, certAlerts, leaveRequests, loading, error, refresh: fetchAll };
}
